import type { CrudRepository } from './repository'

type Entity = { id: string; order: number }

const listenersByKey = new Map<string, Set<(items: unknown[]) => void>>()

function getListeners(storageKey: string) {
  let listeners = listenersByKey.get(storageKey)
  if (!listeners) {
    listeners = new Set()
    listenersByKey.set(storageKey, listeners)
  }
  return listeners
}

function notify<T>(storageKey: string, items: T[]) {
  getListeners(storageKey).forEach((listener) => listener(items))
}

/**
 * localStorage-backed CrudRepository. Shaped to match a future Firestore
 * collection (list/create/update/remove/reorder/subscribe) so swapping the
 * implementation later requires no consumer changes.
 */
export function createLocalStorageRepository<T extends Entity>(
  storageKey: string,
  seed: () => T[],
): CrudRepository<T> {
  function readAll(): T[] {
    const raw = localStorage.getItem(storageKey)
    if (!raw) {
      const seeded = seed()
      writeAll(seeded)
      return seeded
    }

    try {
      const parsed = JSON.parse(raw) as T[]
      return parsed.slice().sort((first, second) => first.order - second.order)
    } catch {
      const seeded = seed()
      writeAll(seeded)
      return seeded
    }
  }

  function writeAll(items: T[]) {
    localStorage.setItem(storageKey, JSON.stringify(items))
    notify(storageKey, items)
  }

  return {
    async list() {
      return readAll()
    },

    async create(input) {
      const items = readAll()
      const nextOrder =
        input.order ?? (items.length > 0 ? Math.max(...items.map((item) => item.order)) + 10 : 10)
      const nextId = input.id ?? crypto.randomUUID()
      const created = { ...input, id: nextId, order: nextOrder } as T

      writeAll([...items, created])
      return created
    },

    async update(id, patch) {
      const items = readAll()
      let updated: T | undefined

      const nextItems = items.map((item) => {
        if (item.id !== id) return item
        updated = { ...item, ...patch }
        return updated
      })

      if (!updated) {
        throw new Error(`No record with id "${id}" in "${storageKey}"`)
      }

      writeAll(nextItems)
      return updated
    },

    async remove(id) {
      const items = readAll()
      writeAll(items.filter((item) => item.id !== id))
    },

    async reorder(orderedIds) {
      const items = readAll()
      const byId = new Map(items.map((item) => [item.id, item]))
      const orderedIdSet = new Set(orderedIds)
      const fullOrderedIds = [
        ...orderedIds,
        ...items.filter((item) => !orderedIdSet.has(item.id)).map((item) => item.id),
      ]
      const reordered = fullOrderedIds
        .map((id, index) => {
          const item = byId.get(id)
          return item ? { ...item, order: (index + 1) * 10 } : undefined
        })
        .filter((item): item is T => Boolean(item))

      writeAll(reordered)
    },

    subscribe(listener) {
      const listeners = getListeners(storageKey) as Set<(items: T[]) => void>
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
